import { useState, useEffect } from "react";
import { User, Menu, X, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "../auth/contexts/AuthContext";
import logoXl from "../assets/logo-xl.svg";
import { Link } from "react-router-dom";

const Navbar = () => {
  const { currentUser, logout, dispatchUserName, dispatchProfileLoading } =
    useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userName, setUserName] = useState("");
  const [userRole, setUserRole] = useState("");

  useEffect(() => {
    const fetchUserData = async () => {
      // Nous n'utilisons plus Firebase pour les rôles ni pour l'affichage,
      // le profil est géré par l'API REST via currentUser.

      if (currentUser) {
        setUserName(currentUser.name || "");
        dispatchUserName(currentUser.name || "");
        // Par défaut, nous pouvons assigner un rôle 'User' ou le récupérer si l'API l'envoie plus tard
        setUserRole(currentUser.role || "User");
        dispatchProfileLoading(false);
      } else {
        dispatchProfileLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser]);

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const handleLogout = async () => {
    try {
      await logout();
      setIsDropdownOpen(false);
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  return (
    <nav className="bg-lime-100 w-full px-4 py-2 mb-0 border-b-0">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        {/* Logo and Title Section */}
        <div className="flex items-center space-x-4">
          <div className="text-white font-bold text-xl flex items-center">
            <img
              src={logoXl}
              alt="CRDB Bank Logo"
              className="w-32 m-auto block"
            />
          </div>
          <div className="text-white text-sm hidden sm:block">
            <div className="text-sm">BETA VERSION</div>
            <div className="text-md font-semibold">CLIMATE RISK ASSESSMENT</div>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <button className="md:hidden text-white" onClick={toggleMobileMenu}>
          {isMobileMenuOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>

        {/* Navigation Links - Desktop */}
        <div className="hidden md:flex items-center space-x-6">
          <Link to="/home" className="text-white hover:font-semibold">
            Home
          </Link>
          <Link to="/about-tanzania" className="text-white hover:font-semibold">
            About Tanzania
          </Link>
          <Link to="/dashboardcard" className="text-white hover:font-semibold">
            Data Room
          </Link>
          <a href="/dashboard/risk" className="text-white hover:font-semibold">
            Risk assessment
          </a>
        </div>

        {/* User Profile Section - Desktop */}
        <div className="hidden md:flex items-center relative">
          <button
            onClick={toggleDropdown}
            className="flex items-center space-x-2 focus:outline-none group"
          >
            <div className="bg-gray-700 rounded-full p-2">
              <User className="h-6 w-6 text-white" />
            </div>
            <div className="text-white justify-items-start">
              <div className="font-semibold">{currentUser?.email}</div>
              <div className="text-sm text-white">{userName || "User"}</div>
            </div>
            {/* Indicateur de direction */}
            <div className="text-white ml-2">
              {isDropdownOpen ? (
                <ChevronUp className="h-5 w-5 transition-transform duration-200" />
              ) : (
                <ChevronDown className="h-5 w-5 transition-transform duration-200" />
              )}
            </div>
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-60 bg-white rounded-md shadow-lg py-1 z-50">
              <Link to="/profile" className="block px-4 py-2 text-green-100">
                My Profile
              </Link>
              {/* <Link
                to="/forgot-password"
                className="block px-4 py-2 text-green-100"
              >
                Change Password
              </Link> */}
              {userRole === "Admin" && (
                <>
                  <Link
                    to="/admin/user-management"
                    className="block px-4 py-2 text-green-100"
                  >
                    User Management
                  </Link>
                  <Link
                    to="/admin/logs"
                    className="block px-4 py-2 text-green-100"
                  >
                    Logs
                  </Link>
                </>
              )}
              <button
                onClick={handleLogout}
                className="block w-full text-left px-4 py-2 text-green-100"
              >
                Log Out
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden mt-4 pb-2">
          <div className="flex flex-col space-y-2">
            <Link
              to="/home"
              className="text-white hover:bg-lime-800 px-3 py-2 rounded-md text-base font-medium transition-colors"
            >
              Home
            </Link>
            <Link
              to="/about-tanzania"
              className="text-white hover:bg-lime-800 px-3 py-2 rounded-md text-base font-medium transition-colors"
            >
              About Tanzania
            </Link>
            <Link
              to="/dashboardcard"
              className="text-white hover:bg-lime-800 px-3 py-2 rounded-md text-base font-medium transition-colors"
            >
              Data Room
            </Link>
            <a
              href="/dashboard/risk"
              className="text-white hover:bg-lime-800 px-3 py-2 rounded-md text-base font-medium transition-colors"
            >
              Risk assessment
            </a>
            <div className="border-t border-green-800 pt-4 mt-2 px-3">
              <div className="text-white mb-4">
                <div className="font-semibold text-base">
                  {currentUser?.email}
                </div>
                <div className="text-sm text-white/80">
                  {userName || "User"}
                </div>
              </div>
              <Link
                to="/profile"
                className="block text-white py-2 text-base font-medium hover:text-lime-200 transition-colors"
              >
                My Profile
              </Link>
              <Link
                to="/forgot-password"
                className="block text-white py-2 text-base font-medium hover:text-lime-200 transition-colors"
              >
                Change Password
              </Link>
              {userRole === "Admin" && (
                <>
                  <Link
                    to="/admin/user-management"
                    className="block text-white py-2 text-base font-medium hover:text-lime-200 transition-colors"
                  >
                    User Management
                  </Link>
                  <Link
                    to="/admin/logs"
                    className="block text-white py-2 text-base font-medium hover:text-lime-200 transition-colors"
                  >
                    Logs
                  </Link>
                </>
              )}
              <button
                onClick={handleLogout}
                className="block text-white py-2 w-full text-left text-base font-medium hover:text-lime-200 transition-colors"
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
